import React, { useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

// Gate 2 v1.0 §6: five Brand Brain domains
const DOMAIN_LABELS = {
  identity: "Identity",
  voice: "Voice",
  positioning: "Positioning",
  visual_direction: "Visual Direction",
  governance: "Governance",
};

// Suggested field_paths per domain. The backend accepts any string; these are
// UX suggestions so Roger can add fields quickly without inventing names.
const DOMAIN_FIELD_SUGGESTIONS = {
  identity: [
    { path: "identity.mission", label: "Mission" },
    { path: "identity.vision", label: "Vision" },
    { path: "identity.values", label: "Values" },
    { path: "identity.origin_story", label: "Origin story" },
    { path: "identity.purpose", label: "Purpose" },
  ],
  voice: [
    { path: "voice.tone", label: "Tone" },
    { path: "voice.register", label: "Register" },
    { path: "voice.cadence", label: "Cadence" },
    { path: "voice.do_say", label: "Do say" },
    { path: "voice.dont_say", label: "Don't say" },
  ],
  positioning: [
    { path: "positioning.category", label: "Category" },
    { path: "positioning.promise", label: "Promise" },
    { path: "positioning.differentiators", label: "Differentiators" },
    { path: "positioning.competitors_note", label: "Competitors note" },
  ],
  visual_direction: [
    { path: "visual_direction.color_direction", label: "Color direction" },
    { path: "visual_direction.typography_direction", label: "Typography direction" },
    { path: "visual_direction.imagery_direction", label: "Imagery direction" },
    { path: "visual_direction.motion_direction", label: "Motion direction" },
  ],
  governance: [
    { path: "governance.forbidden_terms", label: "Forbidden terms" },
    { path: "governance.disclaimers", label: "Disclaimers" },
    { path: "governance.guardrails", label: "Guardrails" },
    { path: "governance.review_cadence", label: "Review cadence" },
  ],
};

// Reasonable defaults that let the confidence engine give High-band on hand-entry.
const DEFAULT_STATEMENT_FORM = {
  field_path: "",
  value: "",
  status: "APPROVED",
  source_type: "founder_input",
  source_type_weight: 3,
  authority: "APPROVED",
  lifecycle_state: "ACTIVE",
  freshness_policy: "EVERGREEN",
  source_agreement: 1,
  recency_days: 0,
  human_reviewed: true,
  ambiguity_flag: false,
  change_reason: "",
};

const BAND_TONE = { High: undefined, Medium: "warn", Low: "danger", Unknown: "muted" };
const READINESS_TONE = { READY: undefined, READY_WITH_GAPS: "warn", NOT_READY: "danger" };
const READINESS_LABEL = { READY: "Ready", READY_WITH_GAPS: "Ready · with gaps", NOT_READY: "Not ready" };

const FRESHNESS_POLICIES = [
  "EVERGREEN",
  "POLICY_CONTROLLED",
  "PRODUCT_CURRENT",
  "MARKET_DYNAMIC",
  "EXTERNAL_DYNAMIC",
];
const AUTHORITY_STATES = ["CANONICAL", "APPROVED", "REFERENCE", "HISTORICAL", "EXTERNAL", "UNVERIFIED"];
const LIFECYCLE_STATES = ["ACTIVE", "DRAFT", "SUPERSEDED", "DEPRECATED"];
const SOURCE_TYPES = [
  "founder_input", "official_site", "product_doc", "policy",
  "press_release", "research", "blog", "external", "user_statement",
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function renderValue(value) {
  if (value === null || value === undefined || value === "") return <em style={{ color: "var(--bo-muted)" }}>—</em>;
  if (Array.isArray(value)) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {value.map((v, i) => <Pill key={`${v}-${i}`}>{String(v)}</Pill>)}
      </div>
    );
  }
  if (typeof value === "object") return <span className="bo-mono" style={{ fontSize: 12 }}>{JSON.stringify(value)}</span>;
  return <span>{String(value)}</span>;
}

function parseValue(input) {
  // If the value contains "," or newline, treat as list; else string.
  if (typeof input !== "string") return input;
  const trimmed = input.trim();
  if (!trimmed) return "";
  if (trimmed.includes("\n") || trimmed.includes(",")) {
    return trimmed.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  }
  return trimmed;
}

function StatementCard({ stmt, onOpenHistory }) {
  const band = stmt.confidence_band || "Unknown";
  const factors = stmt.confidence_factors || {};
  return (
    <div className="bo-card" style={{ padding: 16, marginBottom: 10 }} data-testid={`stmt-card-${stmt.field_path}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "var(--bo-mono)", fontSize: 12, color: "var(--bo-muted)" }}>
          {stmt.field_path}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Pill tone={BAND_TONE[band]} testId={`stmt-band-${stmt.field_path}`}>{band}</Pill>
          <Pill tone="muted">{factors.authority || "—"}</Pill>
          <Pill tone={factors.lifecycle_state === "SUPERSEDED" ? "warn" : "muted"}>{factors.lifecycle_state || "—"}</Pill>
          <Pill tone={factors.freshness_policy === "EVERGREEN" ? undefined : "muted"}>{factors.freshness_policy || "—"}</Pill>
        </div>
      </div>
      <div style={{ marginTop: 10, marginBottom: 8 }}>
        {renderValue(stmt.value)}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 12, color: "var(--bo-muted)" }}>
        <span>v{stmt.version} · updated {fmtDate(stmt.updated_at)}</span>
        <button
          type="button"
          className="bo-btn bo-btn-secondary"
          style={{ padding: "6px 12px", fontSize: 12 }}
          onClick={() => onOpenHistory(stmt)}
          data-testid={`stmt-history-btn-${stmt.field_path}`}
          disabled={(stmt.version || 1) <= 1}
          title={(stmt.version || 1) <= 1 ? "No prior versions yet" : "View version history"}
        >
          History
        </button>
      </div>
    </div>
  );
}

function StatementForm({ domain, brandId, existingPaths, onCreated, onCancel }) {
  const [form, setForm] = useState({ ...DEFAULT_STATEMENT_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const suggestions = DOMAIN_FIELD_SUGGESTIONS[domain] || [];
  const availableSuggestions = suggestions.filter((s) => !existingPaths.includes(s.path));

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.field_path.trim()) { setError("Field path is required."); return; }
    if (!form.field_path.startsWith(`${domain}.`)) {
      setError(`Field path must start with '${domain}.'`);
      return;
    }
    setSaving(true);
    try {
      const value = parseValue(form.value);
      const res = await api.post("/intelligence/statements", {
        scope: "brand",
        scope_id: brandId,
        field_path: form.field_path.trim(),
        value,
        status: form.status,
        source_type: form.source_type,
        source_type_weight: parseInt(form.source_type_weight, 10) || 3,
        authority: form.authority,
        lifecycle_state: form.lifecycle_state,
        freshness_policy: form.freshness_policy,
        source_agreement: parseInt(form.source_agreement, 10) || 1,
        recency_days: parseInt(form.recency_days, 10) || 0,
        human_reviewed: form.human_reviewed,
        ambiguity_flag: form.ambiguity_flag,
        change_reason: form.change_reason || null,
      });
      onCreated(res.data.statement);
      setForm({ ...DEFAULT_STATEMENT_FORM });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{
      background: "var(--bo-panel-alt)", border: "1px solid var(--bo-line)",
      borderRadius: "var(--bo-radius-sm)", padding: 16, marginBottom: 10,
    }} data-testid={`stmt-form-${domain}`}>
      <div className="bo-eyebrow">Add {DOMAIN_LABELS[domain]} statement</div>

      {availableSuggestions.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, marginBottom: 12 }}>
          {availableSuggestions.map((s) => (
            <button
              key={s.path}
              type="button"
              className="bo-btn bo-btn-ghost"
              style={{ padding: "4px 10px", fontSize: 12 }}
              onClick={() => update("field_path", s.path)}
              data-testid={`stmt-suggest-${s.path}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10, marginBottom: 10 }}>
        <label>
          <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 4 }}>Field path</div>
          <input
            className="bo-input"
            value={form.field_path}
            onChange={(e) => update("field_path", e.target.value)}
            placeholder={`${domain}.example`}
            data-testid={`stmt-input-path-${domain}`}
            required
          />
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 4 }}>
            Value (comma or newline = list)
          </div>
          <textarea
            className="bo-input"
            value={form.value}
            onChange={(e) => update("value", e.target.value)}
            placeholder="Type the statement value…"
            rows={3}
            data-testid={`stmt-input-value-${domain}`}
            required
          />
        </label>
      </div>

      <button
        type="button"
        className="bo-btn bo-btn-ghost"
        style={{ padding: "4px 10px", fontSize: 12, marginBottom: 10 }}
        onClick={() => setShowAdvanced((s) => !s)}
        data-testid={`stmt-advanced-toggle-${domain}`}
      >
        {showAdvanced ? "Hide advanced fields" : "Show advanced fields (authority, freshness…)"}
      </button>

      {showAdvanced && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 10 }}>
          <label>
            <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 4 }}>Authority</div>
            <select className="bo-input" value={form.authority} onChange={(e) => update("authority", e.target.value)}>
              {AUTHORITY_STATES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label>
            <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 4 }}>Lifecycle</div>
            <select className="bo-input" value={form.lifecycle_state} onChange={(e) => update("lifecycle_state", e.target.value)}>
              {LIFECYCLE_STATES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label>
            <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 4 }}>Freshness policy</div>
            <select className="bo-input" value={form.freshness_policy} onChange={(e) => update("freshness_policy", e.target.value)}>
              {FRESHNESS_POLICIES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
          <label>
            <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 4 }}>Source type</div>
            <select className="bo-input" value={form.source_type} onChange={(e) => update("source_type", e.target.value)}>
              {SOURCE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
        </div>
      )}

      {showAdvanced && (
        <label style={{ display: "block", marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 4 }}>Change reason (optional)</div>
          <input
            className="bo-input"
            value={form.change_reason}
            onChange={(e) => update("change_reason", e.target.value)}
            placeholder="Why is this being added or changed?"
            data-testid={`stmt-input-reason-${domain}`}
          />
        </label>
      )}

      {error && <div className="bo-login-error" role="alert" data-testid={`stmt-form-error-${domain}`}>{error}</div>}

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button type="submit" className="bo-btn bo-btn-primary" disabled={saving} data-testid={`stmt-submit-${domain}`}>
          {saving ? "Saving…" : "Save statement"}
        </button>
        <button type="button" className="bo-btn bo-btn-ghost" onClick={onCancel} data-testid={`stmt-cancel-${domain}`}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function HistoryDrawer({ statement, onClose }) {
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/intelligence/statements/${statement.id}/history`);
        if (!cancelled) setHistory(res.data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [statement.id]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(24,22,17,0.55)",
        display: "flex", justifyContent: "flex-end", zIndex: 200,
      }}
      data-testid="stmt-history-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100%)", background: "var(--bo-panel)",
          padding: 24, overflowY: "auto", boxShadow: "-8px 0 32px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div className="bo-eyebrow">Version history</div>
            <h2 className="bo-serif" style={{ margin: "4px 0 0 0", fontSize: 22 }}>{statement.field_path}</h2>
          </div>
          <button className="bo-btn bo-btn-ghost" onClick={onClose} data-testid="stmt-history-close">Close</button>
        </div>

        {error && <ErrorBanner error={error} />}
        {!history && !error && <LoadingCard label="Loading history…" />}

        {history && (
          <>
            <div className="bo-card" style={{ padding: 14, marginBottom: 12 }} data-testid="stmt-history-current">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <strong>Current — v{history.statement.version}</strong>
                <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>{fmtDate(history.statement.updated_at)}</span>
              </div>
              {renderValue(history.statement.value)}
            </div>

            {history.history.length === 0 ? (
              <EmptyState title="No prior versions" description="This statement has only been saved once." />
            ) : (
              [...history.history].reverse().map((h) => (
                <div className="bo-card" style={{ padding: 14, marginBottom: 10 }} key={h.id} data-testid={`stmt-history-v${h.version}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span>v{h.version}</span>
                    <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>{fmtDate(h.created_at)}</span>
                  </div>
                  {renderValue(h.snapshot?.value)}
                  {h.change_reason && (
                    <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 8, fontStyle: "italic" }}>
                      Reason: {h.change_reason}
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DomainFilter({ filter, setFilter, counts }) {
  // Segmented control for scoping the domain list. Three modes:
  //   all   — every domain
  //   open  — anything not fully READY (READY_WITH_GAPS + NOT_READY)
  //   ready — READY only
  // Matches the founder's ask: "focus reviews on what's still open".
  const options = [
    { key: "all", label: "All", count: counts.all, testId: "bb-filter-all" },
    { key: "open", label: "Needs work", count: counts.open, testId: "bb-filter-open" },
    { key: "ready", label: "Ready", count: counts.ready, testId: "bb-filter-ready" },
  ];
  return (
    <div
      data-testid="bb-filter-bar"
      role="tablist"
      aria-label="Domain filter"
      style={{
        display: "inline-flex",
        gap: 0,
        marginBottom: 14,
        padding: 3,
        background: "var(--bo-panel-alt)",
        border: "1px solid var(--bo-line)",
        borderRadius: 999,
      }}
    >
      {options.map((o) => {
        const active = filter === o.key;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={o.testId}
            onClick={() => setFilter(o.key)}
            style={{
              padding: "5px 14px",
              fontSize: 12,
              fontWeight: active ? 600 : 400,
              background: active ? "var(--bo-bg)" : "transparent",
              border: "none",
              color: active ? "var(--bo-ink)" : "var(--bo-muted)",
              borderRadius: 999,
              cursor: "pointer",
              transition: "background-color 180ms, color 180ms",
              fontFamily: "inherit",
            }}
          >
            {o.label}
            <span style={{ marginLeft: 6, opacity: 0.75, fontFamily: "var(--bo-mono)", fontSize: 11 }}>
              {o.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function DomainSection({ domain, statements, readiness, brandId, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const existingPaths = statements.map((s) => s.field_path);

  return (
    <section className="bo-card" style={{ marginBottom: 16 }} data-testid={`domain-${domain}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="bo-eyebrow">Brand Brain · Domain</div>
          <h2 className="bo-card-title" data-testid={`domain-title-${domain}`}>{DOMAIN_LABELS[domain]}</h2>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Pill tone={READINESS_TONE[readiness]} testId={`domain-readiness-${domain}`}>{READINESS_LABEL[readiness]}</Pill>
          <span style={{ fontSize: 13, color: "var(--bo-muted)" }} data-testid={`domain-count-${domain}`}>
            {statements.length} statement{statements.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            className="bo-btn bo-btn-secondary"
            style={{ padding: "6px 12px", fontSize: 13 }}
            onClick={() => setShowForm((s) => !s)}
            data-testid={`domain-add-btn-${domain}`}
          >
            {showForm ? "Cancel" : "+ Add statement"}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        {showForm && (
          <StatementForm
            domain={domain}
            brandId={brandId}
            existingPaths={existingPaths}
            onCreated={() => { setShowForm(false); onRefresh(); }}
            onCancel={() => setShowForm(false)}
          />
        )}
        {statements.length === 0 && !showForm ? (
          <EmptyState
            title={`No ${DOMAIN_LABELS[domain]} statements yet`}
            description={`Add ${DOMAIN_LABELS[domain]} statements above, or wait for the Slice 2B extractor to propose them.`}
          />
        ) : (
          statements.map((s) => (
            <StatementCardWrapper key={s.id} stmt={s} onOpenHistory={onRefresh} />
          ))
        )}
      </div>
    </section>
  );
}

// Wraps the card with local history-drawer state so each card manages its own modal.
function StatementCardWrapper({ stmt, onOpenHistory: _refresh }) {
  const [showHistory, setShowHistory] = useState(false);
  return (
    <>
      <StatementCard stmt={stmt} onOpenHistory={() => setShowHistory(true)} />
      {showHistory && <HistoryDrawer statement={stmt} onClose={() => setShowHistory(false)} />}
    </>
  );
}

export default function BrandBrain() {
  const [state, setState] = useState({ loading: true, error: null, brand: null, data: null });
  // Domain filter: "all" (default), "open" (Needs work only), "ready".
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const brandsRes = await api.get("/brands");
      const brand = brandsRes.data.brands?.[0];
      if (!brand) throw new Error("No brand configured for this organization.");
      const grouped = await api.get(`/intelligence/brains/brand/${brand.id}/grouped`);
      setState({ loading: false, error: null, brand, data: grouped.data });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: formatApiError(e) }));
    }
  };

  useEffect(() => { load(); }, []);

  const { brand, data } = state;

  // Apply the domain filter. "open" = anything that isn't fully READY. This
  // is what the founder asked for: "focus reviews on what's still open".
  const filteredDomains = useMemo(() => {
    if (!data?.domains) return [];
    if (filter === "ready") return data.domains.filter((d) => d.readiness === "READY");
    if (filter === "open") return data.domains.filter((d) => d.readiness !== "READY");
    return data.domains;
  }, [data, filter]);

  const counts = useMemo(() => {
    if (!data?.domains) return { all: 0, ready: 0, open: 0 };
    const ready = data.domains.filter((d) => d.readiness === "READY").length;
    return {
      all: data.domains.length,
      ready,
      open: data.domains.length - ready,
    };
  }, [data]);

  return (
    <Shell title="Brand Brain">
      <div className="bo-eyebrow" data-testid="bb-eyebrow">Gate 2 · Slice 2A · Foundation Editor</div>
      <h1
        className="bo-serif"
        style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }}
        data-testid="bb-title"
      >
        Brand Brain
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 720, margin: "0 0 20px 0" }} data-testid="bb-lede">
        The persistent, source-grounded intelligence layer for the brand.
        Hand-enter statements now to seed the extraction pipeline in Slice 2B.
        Every save appends to version history and derives a confidence band deterministically —
        no LLM cost.
      </p>

      <ErrorBanner error={state.error} testId="bb-error" />
      {state.loading && <LoadingCard label="Loading Brand Brain…" />}

      {data && brand && (
        <>
          <div className="bo-card" style={{ marginBottom: 16 }} data-testid="bb-rollup-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div className="bo-eyebrow">Brand</div>
                <h2 className="bo-card-title">{data.brand?.name || brand.name}</h2>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Pill tone={READINESS_TONE[data.rollup_readiness]} testId="bb-rollup-readiness">
                  Brain: {READINESS_LABEL[data.rollup_readiness]}
                </Pill>
                <span style={{ fontSize: 13, color: "var(--bo-muted)" }} data-testid="bb-total-statements">
                  {data.total_statements} statement{data.total_statements === 1 ? "" : "s"} across {data.total_domains || 5} domains
                </span>
              </div>
            </div>
            <p className="bo-card-lede" style={{ marginTop: 12, marginBottom: 0 }}>
              {data.rollup_readiness === "READY" ? (
                <>All five domains have at least three statements. The Brain is production-ready.</>
              ) : data.rollup_readiness === "READY_WITH_GAPS" ? (
                <>
                  Progress visible. Full <em>Ready</em> requires ≥ 3 statements in every domain
                  (Identity · Voice · Positioning · Visual Direction · Governance).
                </>
              ) : (
                <>
                  No statements yet. Start the Adaptive Interview or approve items in the Review Queue
                  to seed the Brain.
                </>
              )}
            </p>
            {(data.empty_domains || []).length > 0 && (
              <div style={{ marginTop: 10, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: "var(--bo-muted)" }} data-testid="bb-empty-label">
                  Still needed:
                </span>
                {data.empty_domains.map((d) => (
                  <Pill key={d} tone="warn" testId={`bb-empty-${d}`}>
                    {d.replace(/_/g, " ")}
                  </Pill>
                ))}
              </div>
            )}
          </div>

          <DomainFilter filter={filter} setFilter={setFilter} counts={counts} />

          {filteredDomains.map((d) => (
            <DomainSection
              key={d.domain}
              domain={d.domain}
              statements={d.statements}
              readiness={d.readiness}
              brandId={brand.id}
              onRefresh={load}
            />
          ))}

          {filteredDomains.length === 0 && (
            <div
              className="bo-card"
              data-testid="bb-filter-empty"
              style={{ textAlign: "center", padding: "32px 16px" }}
            >
              <div style={{ fontSize: 13, color: "var(--bo-muted)", marginBottom: 8 }}>
                {filter === "ready"
                  ? "No fully Ready domains yet."
                  : "No open domains — every domain has ≥ 3 statements."}
              </div>
              <button
                type="button"
                className="bo-btn bo-btn-ghost"
                onClick={() => setFilter("all")}
                data-testid="bb-filter-reset"
                style={{ fontSize: 12, padding: "6px 12px" }}
              >
                Show all domains
              </button>
            </div>
          )}

          {data.other && data.other.length > 0 && (
            <div className="bo-card" data-testid="bb-other-card">
              <div className="bo-eyebrow">Other statements</div>
              <p className="bo-card-lede">
                Statements whose field_path prefix doesn&apos;t match one of the five domains.
                Rename their field_path to fold them into a domain.
              </p>
              {data.other.map((s) => (
                <StatementCardWrapper key={s.id} stmt={s} onOpenHistory={load} />
              ))}
            </div>
          )}
        </>
      )}
    </Shell>
  );
}
